/**
 * NotificationsPage - Full page for viewing all notifications
 *
 * Features:
 * - List of all notifications in reverse chronological order
 * - Mark all as read button
 * - Load more pagination
 * - Deep link navigation
 */

import {
  useListNotifications,
  useMarkAsRead,
  useMarkAllAsRead,
  getListNotificationsQueryKey,
  getGetUnreadCountQueryKey,
  type NotificationsNotificationResponse,
} from '@smarthome/api';
import { useQueryClient } from '@tanstack/react-query';
import { Bell, Check, Loader2 } from 'lucide-react';
import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { markAllNotificationsReadInCache, markNotificationReadInCache } from '../cache';
import { NotificationItem } from '../components/NotificationItem';

import { Button } from '@/components/ui';
import { cn } from '@/components/ui/utils';

const PAGE_SIZE = 20;

export function NotificationsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [offset, setOffset] = useState(0);
  const [allNotifications, setAllNotifications] = useState<NotificationsNotificationResponse[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const prevOffsetRef = useRef(offset);

  // Fetch notifications
  const { data, isLoading, isFetching } = useListNotifications({ limit: PAGE_SIZE, offset });

  // Handle data updates
  useEffect(() => {
    if (data?.notifications) {
      const newNotifications = data.notifications;
      if (offset === 0) {
        setAllNotifications(newNotifications);
      } else if (offset !== prevOffsetRef.current) {
        // Only append if offset actually changed (to avoid double appends)
        setAllNotifications((prev) => [...prev, ...newNotifications]);
      }
      setHasMore(newNotifications.length === PAGE_SIZE);
      prevOffsetRef.current = offset;
    }
  }, [data, offset]);

  // Mark as read mutation
  const { mutate: markAsReadMutation } = useMarkAsRead({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getGetUnreadCountQueryKey() });
        queryClient.invalidateQueries({ queryKey: getListNotificationsQueryKey() });
      },
      onError: () => {
        queryClient.invalidateQueries({ queryKey: getGetUnreadCountQueryKey() });
        queryClient.invalidateQueries({ queryKey: getListNotificationsQueryKey() });
      },
    },
  });

  // Mark all as read mutation
  const { mutate: markAllAsReadMutation, isPending: isMarkingAllRead } = useMarkAllAsRead({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getGetUnreadCountQueryKey() });
        queryClient.invalidateQueries({ queryKey: getListNotificationsQueryKey() });
      },
      onError: () => {
        queryClient.invalidateQueries({ queryKey: getGetUnreadCountQueryKey() });
        queryClient.invalidateQueries({ queryKey: getListNotificationsQueryKey() });
      },
    },
  });

  // Use accumulated notifications or initial data
  const notifications = useMemo(
    () => (allNotifications.length > 0 ? allNotifications : (data?.notifications ?? [])),
    [allNotifications, data?.notifications]
  );
  const hasUnread = notifications.some((notification) => !notification.readAt);

  const handleNotificationClick = useCallback(
    (notification: NotificationsNotificationResponse) => {
      // Mark as read if unread
      if (!notification.readAt && notification.id) {
        const readAt = new Date().toISOString();
        setAllNotifications((prev) =>
          (prev.length > 0 ? prev : notifications).map((item) =>
            item.id === notification.id ? { ...item, readAt: item.readAt || readAt } : item
          )
        );
        markNotificationReadInCache(queryClient, notification.id, readAt);
        markAsReadMutation({ id: notification.id });
      }

      // Navigate to deep link if present
      const context = notification.context;
      const deepLink = context ? context['deepLink'] : undefined;
      if (deepLink && typeof deepLink === 'string') {
        if (deepLink.startsWith('/')) {
          navigate(deepLink);
        } else if (deepLink.startsWith('http')) {
          window.open(deepLink, '_blank');
        }
      }
    },
    [markAsReadMutation, navigate, notifications, queryClient]
  );

  const handleLoadMore = () => {
    setOffset((prev) => prev + PAGE_SIZE);
  };

  const handleMarkAllAsRead = () => {
    const readAt = new Date().toISOString();
    setAllNotifications((prev) =>
      (prev.length > 0 ? prev : notifications).map((notification) => ({
        ...notification,
        readAt: notification.readAt || readAt,
      }))
    );
    markAllNotificationsReadInCache(queryClient, readAt);
    markAllAsReadMutation();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Notifications</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-zinc-400">
            Stay updated with your smart home activity
          </p>
        </div>
        {hasUnread && (
          <Button
            variant="secondary"
            size="sm"
            onClick={handleMarkAllAsRead}
            disabled={isMarkingAllRead}
            leftIcon={
              isMarkingAllRead ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Check className="h-4 w-4" />
              )
            }
          >
            Mark all as read
          </Button>
        )}
      </div>

      {/* Notifications list */}
      <div
        className={cn(
          'overflow-hidden rounded-xl border border-gray-200 bg-white',
          'dark:border-white/[0.06] dark:bg-[#18181b]'
        )}
      >
        {isLoading && offset === 0 ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        ) : notifications.length === 0 ? (
          <div className="py-16 text-center">
            <Bell className="mx-auto h-12 w-12 text-gray-300 dark:text-zinc-600" />
            <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">
              No notifications yet
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-zinc-400">
              When something happens in your smart home, you&apos;ll see it here.
            </p>
          </div>
        ) : (
          <>
            <div className="divide-y divide-gray-100 dark:divide-white/[0.04]">
              {notifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onClick={() => handleNotificationClick(notification)}
                />
              ))}
            </div>

            {/* Load more button */}
            {hasMore && (
              <div className="border-t border-gray-100 p-4 dark:border-white/[0.04]">
                <Button
                  variant="secondary"
                  className="w-full"
                  onClick={handleLoadMore}
                  disabled={isFetching}
                  leftIcon={isFetching ? <Loader2 className="h-4 w-4 animate-spin" /> : undefined}
                >
                  {isFetching ? 'Loading...' : 'Load more'}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
